/**
 * The browser HTTP client for the Madaline control plane (spec §13).
 *
 * Everything under `/api/v1`, JSON in and out, the Better Auth session cookie
 * carried by `credentials: "include"`. CSRF is the API's Origin allowlist on
 * unsafe methods plus the SameSite=Lax session cookie, so a same-origin fetch
 * needs no extra header.
 *
 * Errors are the `{code, message, details?}` envelope §13 defines. They arrive
 * here as a typed `ApiError` so a caller branches on `err.code`
 * (LIVE_NOT_AUTHORIZED, RUN_ACTIVE, entitlement_exceeded) rather than on prose
 * that will be reworded.
 */

/** §13: "All routes under /api/v1". */
export const API_BASE = "/api/v1";

export interface ApiErrorInit {
  status: number;
  code: string;
  message: string;
  details?: unknown;
  cause?: unknown;
}

export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** The `code` from the envelope, or a synthesised one. */
  readonly code: string;
  readonly details: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
  }

  /** The router's redirect-to-/login signal. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** §13 answers 404, never 403, for another tenant's row. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** No response at all: offline, or the API is down. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: FetchLike;
  /** Extra headers on every request (the step-up intent header, in Phase 4). */
  headers?: Record<string, string>;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Query string parameters; undefined and null values are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * A JSON body for `del` — the one method whose signature has no body slot.
   * `DELETE /me` needs one: the account's own address, typed back.
   */
  body?: unknown;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  del<T>(path: string, options?: RequestOptions): Promise<T>;
  /** Absolute URL for a path — the SSE client needs one it can pass to fetch. */
  url(path: string, query?: RequestOptions["query"]): string;
}

function buildQuery(query: RequestOptions["query"]): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  const envelope =
    body && typeof body === "object"
      ? (body as { code?: unknown; message?: unknown; details?: unknown })
      : {};
  return new ApiError({
    status: response.status,
    code: typeof envelope.code === "string" ? envelope.code : `HTTP_${response.status}`,
    message:
      typeof envelope.message === "string" && envelope.message.length > 0
        ? envelope.message
        : `The server answered ${response.status}.`,
    details: envelope.details,
  });
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? API_BASE;
  const fetchImpl: FetchLike = options.fetchImpl ?? ((url, init) => fetch(url, init));

  const url = (path: string, query?: RequestOptions["query"]): string =>
    `${baseUrl}${path}${buildQuery(query)}`;

  async function send<T>(
    method: string,
    path: string,
    body: unknown,
    opts: RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...options.headers,
      ...opts.headers,
    };
    const init: RequestInit = {
      method,
      headers,
      credentials: "include",
    };
    if (opts.signal) init.signal = opts.signal;
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }

    let response: Response;
    try {
      response = await fetchImpl(url(path, opts.query), init);
    } catch (cause) {
      throw new ApiError({
        status: 0,
        code: "NETWORK_ERROR",
        message: "Madaline couldn’t reach the server. Check your connection and try again.",
        cause,
      });
    }

    if (!response.ok) throw await toApiError(response);
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    if (text.length === 0) return undefined as T;
    return JSON.parse(text) as T;
  }

  return {
    get: (path, opts) => send("GET", path, undefined, opts),
    post: (path, body, opts) => send("POST", path, body, opts),
    put: (path, body, opts) => send("PUT", path, body, opts),
    patch: (path, body, opts) => send("PATCH", path, body, opts),
    del: (path, opts) => send("DELETE", path, opts?.body, opts),
    url,
  };
}
